// src/screens/AppointmentDetailScreen.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import QRCode from 'react-qr-code';
import { appointmentsAPI } from '../services/api';

const AppointmentDetailScreen = () => {
  const { id } = useParams(); // appointment id
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await appointmentsAPI.getOne(id);
        setAppointment(data);
      } catch (err) {
        console.error('Error loading appointment', err);
        const msg =
          err.response?.data?.message ||
          err.message ||
          'Failed to load appointment.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Appointment" />
        <div className="flex-1 flex items-center justify-center text-app-text-secondary-light dark:text-app-text-secondary text-sm">
          Loading appointment...
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="flex flex-col h-full">
        <AppHeader title="Appointment" />
        <div className="flex-1 flex flex-col items-center justify-center text-app-text-secondary-light dark:text-app-text-secondary text-sm">
          {error || 'Appointment not found.'}
          <button
            onClick={() => navigate('/appointments')}
            className="mt-3 px-4 py-2 rounded-xl bg-app-accent text-black text-xs font-semibold"
          >
            Back to My Appointments
          </button>
        </div>
      </div>
    );
  }

  const vehicle = appointment.vehicles?.[0];
  const dealer = appointment.dealer;

  const dateStr = appointment.date
    ? new Date(appointment.date).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const timeStr = appointment.time || '';

  const statusColor =
    appointment.status === 'Confirmed'
      ? 'bg-app-success text-black'
      : appointment.status === 'Pending'
      ? 'bg-yellow-400/90 text-black'
      : appointment.status === 'Vehicle Ready'
      ? 'bg-cyan-500/90 text-black'
      : appointment.status === 'Checked In'
      ? 'bg-purple-500/90 text-white'
      : appointment.status === 'Completed'
      ? 'bg-blue-500/90 text-white'
      : appointment.status === 'Canceled'
      ? 'bg-red-500 text-white'
      : appointment.status === 'No Show'
      ? 'bg-orange-500/90 text-white'
      : 'bg-gray-500 text-white';

  return (
    <div className="flex flex-col h-full">
      <AppHeader
        title="Your Test Drive"
        subtitle={dealer?.name || 'Appointment details'}
      />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4 text-sm">
        {/* Dealer + status card */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-sm font-semibold">
                {dealer?.name || 'Dealership'}
              </div>
              {dealer?.address && (
                <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                  {dealer.address.city && dealer.address.state
                    ? `${dealer.address.city}, ${dealer.address.state}`
                    : dealer.address.street || ''}
                </div>
              )}
            </div>
            <span
              className={`px-3 py-1 rounded-full text-[11px] font-semibold ${statusColor}`}
            >
              {appointment.status}
            </span>
          </div>

          {dateStr && (
            <div className="text-xs text-app-text-light dark:text-app-text">
              {dateStr} • {timeStr}
            </div>
          )}
        </div>

        {/* Check-in QR code: shown from booking; the kiosk scans it on the day */}
        {['Pending', 'Confirmed', 'Vehicle Ready'].includes(appointment.status) && appointment.qrToken && (
          <div className="bg-white rounded-2xl p-5 border border-app-border-light dark:border-app-border flex flex-col items-center gap-3">
            <div className="text-sm font-semibold text-gray-900">Your check-in code</div>
            <QRCode value={appointment.qrToken} size={200} level="M" />
            <div className="text-xs text-gray-600 text-center">
              On the day of your test drive, hold this up to the kiosk camera at the dealership.
              {appointment.status === 'Pending' && ' The dealership still needs to confirm your appointment.'}
            </div>
          </div>
        )}

        {/* Status message for other statuses */}
        {['Checked In', 'Completed', 'Canceled', 'No Show'].includes(appointment.status) && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border flex flex-col items-center gap-3">
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              {appointment.status === 'Checked In' && 'You are checked in. Enjoy your test drive!'}
              {appointment.status === 'Completed' && 'This test drive has been completed.'}
              {appointment.status === 'Canceled' && 'This appointment has been canceled.'}
              {appointment.status === 'No Show' && 'This appointment was marked as no show.'}
            </div>
          </div>
        )}

        {/* Vehicle summary */}
        {vehicle && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border flex gap-3 items-center">
            <img
              src={vehicle.image}
              alt={`${vehicle.make} ${vehicle.model}`}
              className="w-20 h-16 rounded-xl object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </div>
              {vehicle.type && (
                <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">{vehicle.type}</div>
              )}
              {vehicle.price != null && (
                <div className="text-xs text-app-accent mt-1">
                  ${vehicle.price.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dealer contact details */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-2">
          <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
            Dealership contact
          </div>

          {dealer?.primaryContactName && (
            <div className="text-xs text-app-text-light dark:text-app-text">
              Contact: {dealer.primaryContactName}
            </div>
          )}
          {dealer?.phone && (
            <div className="text-xs text-app-text-light dark:text-app-text">
              Phone:{' '}
              <a href={`tel:${dealer.phone}`} className="text-app-accent">
                {dealer.phone}
              </a>
            </div>
          )}
          {dealer?.email && (
            <div className="text-xs text-app-text-light dark:text-app-text">
              Email:{' '}
              <a href={`mailto:${dealer.email}`} className="text-app-accent">
                {dealer.email}
              </a>
            </div>
          )}
          {dealer?.website && (
            <div className="text-xs text-app-text-light dark:text-app-text">
              Website:{' '}
              <a
                href={dealer.website}
                target="_blank"
                rel="noreferrer"
                className="text-app-accent"
              >
                {dealer.website}
              </a>
            </div>
          )}
          {dealer?.address && (
            <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
              {dealer.address.street && <div>{dealer.address.street}</div>}
              {(dealer.address.city || dealer.address.state || dealer.address.zip) && (
                <div>
                  {dealer.address.city && `${dealer.address.city}, `}
                  {dealer.address.state && dealer.address.state}{' '}
                  {dealer.address.zip && dealer.address.zip}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Back button */}
        <button
          onClick={() => navigate('/appointments')}
          className="w-full mt-2 py-3 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold"
        >
          Back to My Appointments
        </button>
      </div>
    </div>
  );
};

export default AppointmentDetailScreen;
